import styles from "./Badge.module.css";

/**
 * Deal badges, in the canon vocabulary (`contracts/DESIGN-TOKENS.md` § Badge semantics).
 * They are not interchangeable decoration:
 *
 *   ltd        deal-type signal — genuine lifetime access under the disclosed terms
 *   exclusive  availability signal — exclusive to Dealbid for the represented period
 *   founding   historical community signal — one of the first 10 approved deals
 *   verified   review-status signal — seller association and deal detail were checked
 *
 * Drawn as § Shared component patterns fixes them, which is how `dealbid-web` draws them:
 * a founder looking at their own deal on the desk must see the badge their buyers see on
 * the board. Each carries a distinct **shape** — filled pill, outlined pill, dotted
 * underline, bare check — so canon's "status is never conveyed through colour alone" holds
 * without the four having to be read.
 *
 * This file previously said the same thing and drew something else. It rendered four
 * identical bordered chips with 12px SVG glyphs, which is what the desk was built with in
 * `APP-001` and what `WEB-017` replaced on the board; the claim went stale rather than
 * wrong. `APP-014` made it true again. The glyphs went with the chips they belonged to: four
 * identical marks in a row is most of what made a badge row look busy, and the words were
 * always doing the work.
 *
 * `sponsored` is deliberately absent. It discloses how a *placement* was obtained, and the
 * one surface here that reports placements — the history panel (APP-006) — says how each one
 * was obtained in a sentence instead: canon § 9 makes a rollover free and canon § 10 puts an
 * Honorable Mention outside the Top 10, and a single badge cannot carry those differences.
 */
export type BadgeKind = "ltd" | "exclusive" | "founding" | "verified";

const LABELS: Record<BadgeKind, string> = {
  ltd: "LTD",
  exclusive: "Exclusive",
  founding: "Founding Deal",
  verified: "Deal Verified",
};

export function Badge({ kind }: { kind: BadgeKind }) {
  return (
    /*
     * The check and the word share one element rather than nesting the mark in its own
     * span: a badge whose text is split across elements cannot be found by its text, which
     * is how both a test and a screen-reader user look for it.
     */
    <span className={`${styles.badge} ${styles[kind]}`}>
      {kind === "verified" ? "✓ " : null}
      {LABELS[kind]}
    </span>
  );
}
